import type { Ratebook } from '@api/useFreight/types'
import { useRatebooks } from '@api/useFreight/useFreight'
import { useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import dayjs from 'dayjs'
import * as React from 'react'
import { daysOfWeek } from '../constants'

export type DayOfWeek = 'Sunday' | 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday'

export type BillingFrequency = 'Daily' | 'Weekly' | 'Monthly'

export function useActivationForm(
  ratebook: Ratebook,
  setIsPopoverOpen: (isOpen: boolean) => void,
  setCurrentStatus: (status: string) => void
) {
  const { useCreateRatebookMutation } = useRatebooks()
  const createRatebookMutation = useCreateRatebookMutation()
  const queryClient = useQueryClient()
  const [messageApi, contextHolder] = message.useMessage()

  const [selectedFrequency, setSelectedFrequency] = React.useState<BillingFrequency>(
    (ratebook.invoice_configuration?.billing_frequency || 'Monthly') as BillingFrequency
  )

  const [selectedDay, setSelectedDay] = React.useState<DayOfWeek>(
    (ratebook.invoice_configuration?.invoice_day_of_week || 'Monday') as DayOfWeek
  )

  const currentDate = dayjs()
  const endOfMonth = currentDate.endOf('month')
  const endOfDay = currentDate.endOf('day')

  const nextInvoiceDate = React.useMemo(() => {
    if (selectedFrequency !== 'Weekly') return null

    const currentDay = dayjs().day()
    const targetDay = daysOfWeek.indexOf(selectedDay)
    const daysUntilNext = targetDay <= currentDay ? 7 - (currentDay - targetDay) : targetDay - currentDay

    return dayjs().add(daysUntilNext, 'day').format('YYYY-MM-DD')
  }, [selectedDay, selectedFrequency])

  const handleSubmit = async () => {
    if (
      !ratebook.invoice_configuration ||
      (!ratebook.invoice_configuration.recipients?.length && ratebook.invoice_configuration.send_emails === true)
    ) {
      messageApi.error('Please add an email recipient (or disable emailing invoices) before activating invoicing')
      return
    }

    try {
      const updatedRatebook: Ratebook = {
        ...ratebook,
        invoice_status: 'Active',
        invoice_configuration: {
          ...ratebook.invoice_configuration,
          billing_frequency: selectedFrequency,
          ...(selectedFrequency === 'Weekly' && {
            invoice_day_of_week: selectedDay,
          }),
          activation_date: dayjs().format('YYYY-MM-DD'),
        },
      }

      await createRatebookMutation.mutateAsync(updatedRatebook)
      queryClient.invalidateQueries({ queryKey: ['ratebook', ratebook._id] })
      setCurrentStatus('Active')
      messageApi.success('Invoicing activated successfully')
      setIsPopoverOpen(false)
    } catch (error) {
      console.error('Failed to activate invoicing:', error)
      messageApi.error('Failed to activate invoicing')
    }
  }

  return {
    selectedFrequency,
    setSelectedFrequency,
    selectedDay,
    setSelectedDay,
    nextInvoiceDate,
    endOfMonth,
    endOfDay,
    handleSubmit,
    createRatebookMutation,
    contextHolder,
  }
}
